'use client';

import { AllSubKeysMessageResource } from '@/i18n/messages';
import { cn } from '@/lib/utils';
import { Link, usePathname } from '@/navigation';
import { ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { arrHeaderNavbar } from '../../common';

const HeaderNavbar = ({ className }: { className?: string }) => {
    const pathname = usePathname();
    const transSidebar = useTranslations('Sidebar');

    return (
        <nav
            className={cn(
                'hidden h-full w-full justify-center px-1 font-bold xl:flex 2xl:gap-x-8',
                className
            )}
        >
            {arrHeaderNavbar.map((menu, index) => {
                const Component = menu.icon;
                const isHvChild = menu.children && menu.children.length;
                const isActive =
                    pathname === menu.href ||
                    (isHvChild &&
                        menu.children?.some(
                            (child) => child.href === pathname
                        ));

                const menuContent = (
                    <div
                        className={cn(
                            'flex items-center justify-center gap-x-2 rounded-full bg-primary-foreground px-3',
                            isActive
                                ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                                : 'text-primary hover:bg-secondary/20'
                        )}
                    >
                        {Component && (
                            <Component
                                strokeWidth={2}
                                className='h-4 w-4 min-w-4 transition-all group-hover:scale-110'
                            />
                        )}
                        <span
                            className={cn(
                                'line-clamp-1 max-w-16 text-sm lg:max-w-24 lg:text-base xl:max-w-56 xl:text-lg 2xl:max-w-64'
                            )}
                        >
                            {menu.langResource
                                ? transSidebar(
                                      menu.langResource as AllSubKeysMessageResource<'Sidebar'>
                                  )
                                : menu.name}
                        </span>
                        {isHvChild && (
                            <ChevronRight
                                strokeWidth={2}
                                className='h-4 w-4 min-w-4 transition-all group-hover:rotate-90'
                            />
                        )}
                    </div>
                );

                return (
                    <div
                        key={index}
                        className={cn(
                            'group relative flex h-full items-center'
                        )}
                    >
                        {menu.href ? (
                            <Link
                                href={menu.href}
                                className={cn(
                                    'group relative flex items-center gap-x-1',
                                    'transition-all hover:text-secondary',
                                    'duration-200 after:absolute after:left-6 after:top-full after:border-b-2 after:border-b-secondary after:pt-1 lg:after:w-0 lg:after:transition-all',
                                    isActive
                                        ? 'text-secondary lg:after:w-[70%]'
                                        : 'lg:hover:after:w-1/2'
                                )}
                            >
                                {menuContent}
                            </Link>
                        ) : (
                            <div
                                className={cn(
                                    'relative flex cursor-pointer items-center gap-x-2',
                                    'transition-all hover:text-secondary'
                                )}
                            >
                                {menuContent}
                            </div>
                        )}

                        {isHvChild && (
                            <div
                                className={cn(
                                    'absolute left-0 top-full z-10 w-52 translate-y-1',
                                    'pointer-events-none translate-y-2 opacity-0',
                                    'group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100',
                                    'transition-all duration-200 ease-out'
                                )}
                            >
                                {/* Arrow tip */}
                                <div className='ml-6 h-2 w-2 rotate-45 border-l border-t shadow-sm' />

                                {/* Dropdown box */}
                                <div className='-mt-1 space-y-1 overflow-hidden rounded-md border border-gray-100 bg-white shadow-lg'>
                                    {menu.children?.map((child, childIndex) => {
                                        const ChildIcon = child.icon;
                                        const isActive =
                                            pathname === child.href;

                                        const childContent = (
                                            <>
                                                {ChildIcon && (
                                                    <ChildIcon
                                                        strokeWidth={2}
                                                        className='h-4 w-4 min-w-4'
                                                    />
                                                )}
                                                <span className='line-clamp-1'>
                                                    {child.langResource
                                                        ? transSidebar(
                                                              child.langResource as AllSubKeysMessageResource<'Sidebar'>
                                                          )
                                                        : child.name}
                                                </span>
                                            </>
                                        );

                                        const childClassName = cn(
                                            'flex items-center gap-x-2 px-4 py-2.5',
                                            'transition-colors duration-150',
                                            'hover:bg-amber-50 hover:text-secondary',
                                            isActive
                                                ? 'bg-amber-50 text-secondary'
                                                : 'text-gray-700'
                                        );

                                        return child.href ? (
                                            <Link
                                                key={childIndex}
                                                href={child.href}
                                                className={childClassName}
                                            >
                                                {childContent}
                                            </Link>
                                        ) : (
                                            <div
                                                key={childIndex}
                                                className={childClassName}
                                            >
                                                {childContent}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </nav>
    );
};

export default HeaderNavbar;
